import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, withQuery } from "@/lib/api";
import { useApiMutation, useApiQuery } from "@/lib/query";
import type {
  Dataset,
  DatasetCreate,
  DatasetItem,
  DatasetSource,
  Page,
  SavedVersion,
  SourceUpdate,
  VersionCreate,
} from "./types";

export const useDatasets = () => useApiQuery<Dataset[]>("/datasets");

export const useDataset = (id: string) => useApiQuery<Dataset>(`/datasets/${id}`);

export const useDatasetItems = (id: string, version: number | null, page: number) =>
  useApiQuery<Page<DatasetItem>>(
    `/datasets/${id}/versions/${version}/items`,
    { page, page_size: 100 },
    { enabled: version !== null, placeholderData: keepPreviousData },
  );

/** Every item of one version, fetched page by page (a version holds at most 5,000). */
export function useAllDatasetItems(id: string | null, version: number | null) {
  return useQuery({
    queryKey: [`/datasets/${id}/versions/${version}/items`, { all: true }],
    queryFn: async () => {
      const out: DatasetItem[] = [];
      for (let page = 1; page <= 10; page++) {
        const path = withQuery(`/datasets/${id}/versions/${version}/items`, { page, page_size: 500 });
        const res = await api.get<Page<DatasetItem>>(path);
        out.push(...res.items);
        if (out.length >= res.total || res.items.length === 0) break;
      }
      return out;
    },
    enabled: !!id && version !== null,
  });
}

export const useCreateDataset = () =>
  useApiMutation<Dataset, DatasetCreate>("post", "/datasets", { invalidate: ["/datasets"] });

// The dataset's latest version, its item count and every list showing it all
// changed, so the whole family refetches; so does the agent's check of these items.
export const useCreateVersion = (id: string) =>
  useApiMutation<SavedVersion, VersionCreate>("post", `/datasets/${id}/versions`, {
    invalidate: ["/datasets"],
  });

// Only the golden flag for now (information only: nothing else reads it). The
// body is hand-typed until the PATCH route lands in the generated schema.
export const useUpdateDataset = (id: string) =>
  useApiMutation<Dataset, { is_golden: boolean }>("patch", `/datasets/${id}`, {
    invalidate: ["/datasets"],
  });

/** A live dataset's trace source (static datasets have none). */
export const useDatasetSource = (id: string, enabled = true) =>
  useApiQuery<DatasetSource>(`/datasets/${id}/source`, undefined, { enabled });

/** Changes what the next run pulls; versions already pulled stay as they are. */
export const useUpdateSource = (id: string) =>
  useApiMutation<DatasetSource, SourceUpdate>("patch", `/datasets/${id}/source`, {
    invalidate: [`/datasets/${id}/source`],
  });
